import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Swords, Award } from "lucide-react";
import { useLeagueContext } from "@/modules/leagues/hooks/useLeagueContext";
import { useAvailableRounds, useMatchesByRound } from "@/modules/leagues/hooks/useMatches";
import { useRoundBonuses } from "@/modules/leagues/hooks/useRoundBonuses";
import { useBonusQuestionsConfig } from "@/modules/leagues/hooks/useBonusQuestions";
import { hasAnyActiveQuestion } from "@/modules/leagues/types/bonusQuestions";
import { MatchCard } from "@/modules/leagues/components/MatchCard";
import { LiveMatchCard } from "@/modules/leagues/components/LiveMatchCard";
import { SkeletonCard } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import { StadiumIllustration } from "@/components/atoms/illustrations";
import { ErrorState } from "@/components/organisms/ErrorState";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import type { Match } from "@/modules/leagues/types/match";
import { isLikelyStillLive, isStaleScheduledPastKickoff } from "@/modules/leagues/repositories/shared/matchPredictionsHelpers";

export function LeaguePredictionsPage() {
  const { league } = useLeagueContext();
  const navigate = useNavigate();
  const { data: rounds } = useAvailableRounds(league.id);
  const { data: bonusConfig } = useBonusQuestionsConfig(league.id);
  const [selectedRound, setSelectedRound] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<"live" | "scheduled" | "finished" | undefined>(undefined);

  const { data: matches, isLoading, isError, refetch } = useMatchesByRound(league.id, selectedRound);
  const currentRoundNumber = selectedRound ?? matches?.[0]?.round;
  const { data: roundBonuses } = useRoundBonuses(league.id, currentRoundNumber);
  const bonusByMatchId = new Map((roundBonuses ?? []).map((b) => [b.matchId, b.type]));

  // Separação Ao vivo / Futuras / Encerradas (pedido do usuário, com
  // referência visual de outro app) — mesmo dado de sempre
  // (useMatchesByRound), só filtrado por status na hora de exibir, nunca
  // uma busca paralela.
  //
  // NUNCA confia só no `status` cru da API pra decidir a aba — a mesma
  // razão documentada em `predictionsLocked` (a API pode demorar a virar
  // "IN_PLAY" mesmo com o jogo já rolando de verdade, sobretudo no plano
  // gratuito do football-data.org). MAS isso tem um teto (ver
  // `isLikelyStillLive`/`MAX_HOURS_TO_ASSUME_STILL_LIVE`) — bug real
  // relatado: sem esse teto, uma partida de ONTEM presa em "SCHEDULED"
  // (dado desatualizado da API) era classificada como "Ao vivo" HOJE,
  // porque a checagem só perguntava "já passou do horário?" sem limite
  // de quanto tempo atrás. Fora da janela razoável, trata como
  // provavelmente encerrada — mais honesto que continuar chamando de
  // "ao vivo" indefinidamente.
  const hasKickedOff = (m: Match) => new Date(m.kickoff).getTime() <= Date.now();
  const liveMatches = (matches ?? []).filter(isLikelyStillLive);
  const scheduledMatches = (matches ?? []).filter(
    (m) => (m.status === "scheduled" && !hasKickedOff(m)) || m.status === "postponed"
  );
  const finishedMatches = (matches ?? []).filter((m) => m.status === "finished" || isStaleScheduledPastKickoff(m));
  const STATUS_TABS = [
    { key: "live" as const, label: "Ao vivo", list: liveMatches },
    { key: "scheduled" as const, label: `Futuras (${scheduledMatches.length})`, list: scheduledMatches },
    { key: "finished" as const, label: "Encerradas", list: finishedMatches },
  ];
  // Default inteligente (só na primeira carga, sem sobrescrever escolha do
  // usuário): prioriza Ao vivo > Futuras > Encerradas — quem abre a tela
  // quer ver o que importa agora, não decidir uma aba antes de ver nada.
  const effectiveStatusFilter =
    statusFilter ?? (liveMatches.length > 0 ? "live" : scheduledMatches.length > 0 ? "scheduled" : "finished");
  const effectiveVisibleMatches = STATUS_TABS.find((t) => t.key === effectiveStatusFilter)?.list ?? [];

  // Min/máx REAIS a partir dos números de rodada retornados pela API — nunca
  // `rounds.length`, que é só a quantidade de itens e não bate com o número
  // da rodada quando ela não começa em 1 ou tem lacunas (Priority 4 do
  // escopo "bolão funcional de verdade": navegação estava quebrada por isso).
  const minRound = rounds?.[0]?.round;
  const maxRound = rounds?.[rounds.length - 1]?.round;

  return (
    <div className="space-y-4">
      {league.permiteDesafios && (
        <button
          onClick={() => navigate(ROUTES.duels(league.id))}
          className="flex w-full items-center justify-between rounded-xl border border-accent-500/40 bg-accent-50 px-4 py-2.5 text-left shadow-glow-accent transition-colors hover:bg-accent-100 dark:bg-accent-500/10 dark:hover:bg-accent-500/20"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-accent-700 dark:text-accent-300">
            <Swords className="h-4 w-4" /> Duelos 1x1
          </span>
          <ChevronRight className="h-4 w-4 text-accent-700 dark:text-accent-300" />
        </button>
      )}

      {hasAnyActiveQuestion(bonusConfig) && (
        <button
          onClick={() => navigate(ROUTES.bonusQuestionsAnswer(league.id))}
          className="flex w-full items-center justify-between rounded-xl border border-primary/30 bg-primary-50 px-4 py-2.5 text-left transition-colors hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-primary">
            <Award className="h-4 w-4" /> Perguntas Bônus
          </span>
          <ChevronRight className="h-4 w-4 text-primary" />
        </button>
      )}

      {rounds && rounds.length > 0 && currentRoundNumber !== undefined && (
        <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2">
          <button
            disabled={minRound !== undefined && currentRoundNumber <= minRound}
            onClick={() => setSelectedRound(currentRoundNumber - 1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground disabled:opacity-30"
            aria-label="Rodada anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="font-display text-sm font-semibold">Rodada {currentRoundNumber}</span>
          <button
            disabled={maxRound !== undefined && currentRoundNumber >= maxRound}
            onClick={() => setSelectedRound(currentRoundNumber + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground disabled:opacity-30"
            aria-label="Próxima rodada"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {matches && matches.length > 0 && (
        <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatusFilter(t.key)}
              className={cn(
                "flex-1 whitespace-nowrap rounded-lg py-1.5 px-2 text-xs font-semibold font-display transition-colors",
                effectiveStatusFilter === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="space-y-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {isError && !matches && (
        <ErrorState
          title="Não foi possível carregar os jogos"
          description="Verifique sua conexão e tente novamente."
          onRetry={() => refetch()}
        />
      )}

      {isError && matches && (
        <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-center text-xs text-amber-800 dark:text-warning">
          Não deu pra atualizar agora — mostrando os últimos dados carregados.
        </p>
      )}

      {matches && matches.length === 0 && !isLoading && (
        <EmptyState
          illustration={StadiumIllustration}
          title="Nenhuma partida nesta rodada"
          description="Assim que o calendário oficial for liberado, os jogos aparecem aqui."
        />
      )}

      {matches && matches.length > 0 && effectiveVisibleMatches.length === 0 && (
        <EmptyState illustration={StadiumIllustration} title="Nenhum jogo encontrado" description="Não há partidas nesta categoria para a rodada selecionada." />
      )}

      {effectiveVisibleMatches.length > 0 && (
        <div className="space-y-3">
          {effectiveVisibleMatches.map((match) =>
            effectiveStatusFilter === "live" ? (
              <LiveMatchCard
                key={match.id}
                match={match}
                scoringRules={league.rules.rules}
                roundBonus={bonusByMatchId.get(match.id) ?? null}
                onOpenDetail={() => navigate(ROUTES.matchDetail(league.id, match.id))}
              />
            ) : (
              <MatchCard
                key={match.id}
                match={match}
                leagueId={league.id}
                scoringRules={league.rules.rules}
                totalParticipants={league.participantsCount}
                roundBonus={bonusByMatchId.get(match.id) ?? null}
                onOpenDetail={() => navigate(ROUTES.matchDetail(league.id, match.id))}
                onChallenge={league.permiteDesafios ? () => navigate(ROUTES.createDuel(league.id, match.id)) : undefined}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}
